import { useRouter } from 'next/router';
import { useMemo } from 'react';
import type { ParsedUrlQuery } from 'querystring';

/**
 * Next will only add the query parameters after the first render.
 * extracts them from router.pathname / router.asPath and window.location.search
 * so they are available in the browser immediately
 */

export const useNextQueryParams = (): ParsedUrlQuery => {
  const router = useRouter();
  return useMemo(() => {
    if (router.isReady) {
      return router.query;
    }
    const pathKeysSplit = router.pathname.split('/').filter(Boolean);
    const pathKeys: [number, string][] = pathKeysSplit
      .filter(k => k.indexOf('[') === 0 && k.indexOf(']') === k.length - 1)
      .map(k => {
        const key = k.replace('[', '').replace(']', '');
        return [pathKeysSplit.indexOf(k), key];
      });
    const pathValues = router.asPath.split('?')[0].split('/').filter(Boolean);
    const query = pathKeys.reduce((prev, [index, key]) => {
      const previousPathKey = prev;
      previousPathKey[key] = pathValues[index];
      return previousPathKey;
    }, {} as ParsedUrlQuery);
    if (typeof window !== 'undefined') {
      const otherParams = Object.fromEntries(new URLSearchParams(window.location.search).entries());
      Object.assign(query, otherParams);
    }

    return query;
  }, [router.isReady, router.query, router.asPath, router.pathname]);
};

export const useNextHashQueryParams = (): ParsedUrlQuery => {
  const router = useRouter();
  return useMemo(() => {
    if (router.isReady) {
      return Object.fromEntries(new URLSearchParams(router.asPath.split('#')[1]));
    }

    let query = {} as ParsedUrlQuery;
    if (typeof window !== 'undefined') {
      query = Object.fromEntries(new URLSearchParams(window.location.hash.split('#')[1]));
    }
    return query;
  }, [router.isReady, router.asPath]);
};

export const createHashParam = (routerQuery: ParsedUrlQuery) => {
  const hash = new URLSearchParams();
  Object.keys(routerQuery).forEach(key => {
    hash.append(key, routerQuery[key] as string);
  });
  return hash;
};
